import React, { useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import {
    CodePrintingState,
    codePrintingContext,
} from "../UsersCodePrintingContext";

import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import { commonMessages } from "../../../utils/messages";

const Confirmation = () => {
    const intl = useIntl();
    const [studentsCodesPrinting, setStudentsCodesPrinting] =
        useContext(codePrintingContext);

    const clear = () => {
        setStudentsCodesPrinting((curr) => ({
            ...curr,
            showSelection: false,
            selectedStudents: [],
        }));
    };

    const print = () => {
        setTimeout(() => {
            window.print();
            clear();
        }, 100);
    };

    if (!studentsCodesPrinting.showSelection) {
        return null;
    }

    return (
        <div className="user-list__student__actions">
            <Button
                label={intl.formatMessage(commonMessages.cancel)}
                onPress={clear}
            />
            <Button
                disabled={!hasSelection(studentsCodesPrinting)}
                label={intl.formatMessage(messages.downloadStudents, {
                    studentsCount:
                        studentsCodesPrinting.selectedStudents.length,
                })}
                onPress={print}
            />
        </div>
    );
};

export const Checkbox = ({ studentId }: { studentId: string }) => {
    const [{ selectedStudents }, setCodePrinting] =
        useContext(codePrintingContext);

    const toggleStudent = (id: string) => () => {
        setCodePrinting(({ selectedStudents, ...curr }) => ({
            ...curr,
            selectedStudents: selectedStudents.includes(id)
                ? selectedStudents.filter((existingId) => existingId !== id)
                : [...selectedStudents, id],
        }));
    };

    return (
        <button
            className="user-list__student__checkbox"
            role="checkbox"
            aria-checked={selectedStudents.includes(studentId)}
            onClick={toggleStudent(studentId)}
            tabIndex={0}
        >
            <Icon
                className="user-list__student__checkbox__icon --checked"
                path="checkbox_square_unchecked"
            />
            <Icon
                className="user-list__student__checkbox__icon --unchecked"
                path="checkbox_square_checked"
            />
        </button>
    );
};

export const GlobalCheckbox = ({ className }: { className: string }) => {
    const [codePrinting, setCodePrinting] = useContext(codePrintingContext);
    const checked = getCheckStatus(codePrinting);

    const selectAll = () => {
        setCodePrinting((curr) => ({
            ...curr,
            selectedStudents: curr.students,
        }));
    };

    const clearSelection = () => {
        setCodePrinting((curr) => ({ ...curr, selectedStudents: [] }));
    };

    const onClick = () => {
        if (
            codePrinting.selectedStudents.length ===
            codePrinting.students.length
        ) {
            clearSelection();
        } else {
            selectAll();
        }
    };

    return (
        <button
            className={className}
            role="checkbox"
            aria-checked={checked}
            onClick={onClick}
            tabIndex={0}
        >
            <Icon
                className="user-list__student__checkbox__icon --unchecked"
                path="checkbox_square_unchecked"
            />
            <Icon
                className="user-list__student__checkbox__icon --checked"
                path="checkbox_square_checked"
            />
            <Icon
                className="user-list__student__checkbox__icon --some-checked"
                path="checkbox_square_some_selected"
            />
        </button>
    );
};

export default {
    Confirmation,
    Checkbox,
    GlobalCheckbox,
};

const hasSelection = ({
    selectedStudents,
    students,
}: Pick<CodePrintingState, "selectedStudents" | "students">) => {
    return (
        selectedStudents.length !== 0 &&
        selectedStudents.length <= students.length
    );
};

const getCheckStatus = ({
    selectedStudents,
    students,
}: Pick<CodePrintingState, "selectedStudents" | "students">) => {
    const selectedStudentsCount = selectedStudents.length;
    if (selectedStudentsCount === students.length) return true;
    if (selectedStudentsCount !== 0 && selectedStudentsCount < students.length)
        return "mixed" as const;
    return false;
};

const messages = defineMessages({
    downloadStudents: {
        id: "user-list-downloadStudents",
        defaultMessage: "Download students card ({studentsCount})",
    },
});
